/** @format */
import { css } from 'styled-components';

import { theme } from '@toucan/client/global/theme';

import { SelectProps, SelectSize, SelectVariant } from './index';

/**
 * Base styles for the Select component
 */
/* eslint-disable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
const baseStyles = () => css`
    position: relative;
    display: block;
    width: 100%;
    height: 4.8rem;
    margin-top: var(--layout-spacing-24);
    z-index: 2;
    border: 0;
    background: transparent;
    font-size: 1.1rem;
    font-weight: normal;
    line-height: normal;
    cursor: pointer;

    // visually hidden but still visible to screen reader
    color: rgba(255, 255, 255, 0);
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;

    &::-ms-expand {
        display: none;
    }

    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
        // IE11 visually hidden but visible to screen reader
        opacity: 0.0001;
    }

    // fix left position in firefox
    @supports (-moz-appearance: none) {
        & {
            left: -0.4rem;
        }
    }

    &:focus {
        box-shadow: none;
        outline: 0;
    }

    &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 #000;
    }

    // This is because we use a custom label for the select
    // element and we need to override the built-in style to
    // ensure the default element is hidden
    &:-webkit-autofill,
    &:-webkit-autofill:focus {
        -webkit-text-fill-color: transparent;
    }

    // Label base styles
    & + label {
        ${theme.typography.body_small_highlight};
        color: var(--colors-text-secondary);
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        top: calc(var(--layout-spacing-24) * -1);
        z-index: 1;
        padding-bottom: 0.8rem;
    }

    &[data-value=''] + label {
        color: var(--colors-text-tertiary);
    }

    & ~ .text {
        display: block;
        position: absolute;
        bottom: var(--layout-spacing-12);
        left: var(--layout-spacing-12);
        ${theme.typography.body_normal}
        color: var(--colors-text-secondary);
        z-index: 2;
        width: 100%;
        pointer-events: none;
    }

    &[data-value=''] ~ .text {
        color: var(--colors-text-tertiary);
    }

    // ellipses for long labels and text
    & + label,
    & ~ .text {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & ~ .arrow {
        position: absolute;
        right: var(--layout-spacing-12);
        bottom: var(--layout-spacing-12);
        z-index: 2;
        pointer-events: none;
        transition-duration: 0s;
    }

    option {
        color: var(--colors-text-secondary);
    }

    /**
     * To stop ios zooming select elements when clicking on them
     * they need to have a min font size of 16px
     *
     * https://stackoverflow.com/a/16255670
     */
    @supports (-webkit-overflow-scrolling: touch) {
        font-size: 1.6rem;
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */

/**
 * The styles for the select variant.
 *
 * @param {SelectProps}   props           The select properties object
 * @param {SelectVariant} props.variant   The select variant
 * */
const variantStyles = ({ variant }: SelectProps) => {
    switch (variant) {
        case SelectVariant.INLINE:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                height: auto;
                margin-top: 0;
                box-sizing: content-box;

                & + label,
                & ~ .text {
                    position: static;
                    display: inline-block;
                    width: auto;
                }

                & + label {
                    font-weight: 600;
                    margin-right: 0.8rem;
                    padding-bottom: 0;
                    color: var(--colors-text-secondary);
                }

                & ~ .text {
                    max-width: 20rem;
                    color: var(--colors-text-primary);
                }

                &:hover ~ .text,
                &:focus ~ .text {
                    color: var(--colors-text-secondary);
                }

                & ~ svg {
                    position: relative;
                    top: 0.1rem;
                    right: -1.5rem;
                    transform: rotate(90deg);
                    fill: var(--colors-text-tertiary);
                }

                &:hover ~ svg {
                    fill: var(--colors-text-secondary);
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

        case SelectVariant.BLOCK:
        default:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                padding: var(--layout-spacing-12);
                border: 0.1rem solid var(--colors-border-primary);
                border-radius: 0.4rem;
                background-color: var(--colors-surface-primary);

                // ellipses for long labels and text
                & + label,
                & ~ .text {
                    padding-right: 0.8rem;
                }

                &:hover {
                    box-shadow: var(--effects-shadow-lg);
                }

                &:active,
                &:focus {
                    border-color: var(--colors-text-secondary);
                    box-shadow: inset 0 0 0 0.05rem
                        var(--colors-surface-inverse);

                    + label {
                        color: var(--colors-text-secondary);
                    }

                    ~ .arrow {
                        transform: rotate(180deg);
                        background-color: var(--colors-text-secondary);
                        svg {
                            fill: var(--colors-text-inverse);
                        }
                    }
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
    }
};

/**
 * The styles for the select size.
 *
 * @param {SelectProps}   props           The select properties object
 * @param {SelectSize} props.displaySize   The select size
 * */
const sizeStyles = ({ displaySize }: SelectProps) => {
    switch (displaySize) {
        case SelectSize.SMALL:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                & + label,
                & ~ .text {
                    font-size: 1.2rem;
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
        case SelectSize.MEDIUM:
        default:
            return css``;
    }
};

/**
 * The styles for the select arrow.
 *
 * @param {SelectProps} props           The select properties object
 * @param {SelectSize}  props.displayArrow   Whether or not to display the arrow
 * */
const arrowStyles = ({ displayArrow }: SelectProps) => {
    if (!displayArrow) {
        return css`
            padding-right: 0;
        `;
    }

    return css``;
};

/**
 * Styles for when the select is disabled.
 *
 * @param {SelectProps}  props           The select properties object
 * @param {boolean}      props.disabled  True if the select is disabled
 * */
const disabledStyles = ({ disabled }: SelectProps) => {
    if (!disabled) {
        return css``;
    }

    return css`
        &:hover {
            box-shadow: none;
        }

        &,
        &:active,
        &:focus,
        &:hover {
            background: var(--colors-surface-disabled);
            cursor: default;
            border-color: var(--colors-border-disabled);

            & + label {
                font-weight: 400;
            }

            & + label,
            & ~ .text {
                color: var(--colors-text-tertiary);
            }
        }

        & ~ .arrow {
            border-color: var(--colors-text-tertiary);

            svg {
                fill: var(--colors-text-disabled);
            }
        }
    `;
};

/**
 * Styles for when we want the select to fill its parent container
 *
 * @param {SelectProps}  props             The select properties object
 * @param {boolean}      props.fillParent  Whether or not to fill parent
 * */
const fillParentStyles = ({ fillParent }: SelectProps) => {
    if (fillParent) {
        return css`
            > div {
                width: 100%;
                height: 100%;
            }
        `;
    }

    return css``;
};

/**
 * Styles for when the select has an invalid value.
 *
 * @param {SelectProps}  props           The select properties object
 * @param {string}       props.error     The error message
 * @param {boolean}      props.touched   True if the input has been touched
 * @param {boolean}      props.disabled  True if the select is disabled
 * */
const errorStyles = ({ error, touched, disabled }: SelectProps) => {
    if (!error || !touched || disabled) {
        return css``;
    }

    return css`
        &,
        &[data-value=''],
        &:hover,
        &:focus,
        &:active {
            background-color: var(--colors-state-error-light);
            border-color: var(--colors-state-error);
            box-shadow: inset 0 0 0 0.05rem var(--colors-state-error);

            & + label,
            &[data-value=''] + label,
            & ~ .text,
            &[data-value=''] ~ .text {
                color: var(--colors-state-error);
            }
        }

        &:focus {
            & ~ .arrow {
                background-color: var(--colors-state-error);
            }
        }

        & ~ .arrow {
            border-color: var(--colors-state-error);

            svg {
                fill: var(--colors-state-error);
            }
        }
    `;
};

/**
 * The Select component styles
 *
 * @param {SelectProps} props The select properties object
 */
export const styles = (props: SelectProps) => css`
    ${baseStyles()}
    ${variantStyles(props)}
    ${sizeStyles(props)}
    ${arrowStyles(props)}
    ${fillParentStyles(props)}
    ${disabledStyles(props)}
    ${errorStyles(props)}
`;
